import { FC } from 'react';
import {
  Datagrid,
  ListBase,
  Pagination,
  ReferenceField,
  TextField,
  useCreatePath,
  useListContext,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import { Box, Button, InputAdornment, TextField as MuiTextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { Link, useNavigate } from 'react-router-dom';

import { DetachFromChannelBulkAction } from './DetachFromChannelBulkAction';
import { AssignSerialsToChannelButton } from './AssignSerialsToChannelButton';

const UnitEditButton = () => {
  const record = useRecordContext();
  const createPath = useCreatePath();
  if (!record) return null;
  const editPath = createPath({ resource: 'units', type: 'edit', id: record.id }) + '/info';
  return (
    <Button component={Link} to={editPath} size="small" startIcon={<EditIcon />}>
      Edit
    </Button>
  );
};

interface UpdateChannelUnitsTabProps {
  channelId: number;
}

interface UnitsListContentProps {
  channelId: number;
}

const UnitsListContent: FC<UnitsListContentProps> = ({ channelId }) => {
  const translate = useTranslate();
  const { total, isLoading, setFilters, filterValues } = useListContext();
  const createPath = useCreatePath();
  const navigate = useNavigate();

  const handleRowClick = (id: any): false => {
    navigate(createPath({ resource: 'units', type: 'show', id }) + '/info');
    return false;
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filterValues, q: event.target.value }, {});
  };

  const isEmpty = !isLoading && total === 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <AssignSerialsToChannelButton channelId={channelId} />
        <MuiTextField
          size="small"
          placeholder={translate('resources.update_channels.units.search_placeholder')}
          value={filterValues.q ?? ''}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 260 }}
        />
      </Box>

      {isEmpty ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {translate('resources.update_channels.units.empty')}
        </Typography>
      ) : (
        <>
          <Datagrid
            rowClick={handleRowClick}
            bulkActionButtons={<DetachFromChannelBulkAction currentChannelId={channelId} />}
          >
            <TextField source="serial" />
            <TextField source="hostname" />
            <ReferenceField source="hardware_revision_id" reference="hardware_revisions" link={false}>
              <TextField source="name" />
            </ReferenceField>
            <UnitEditButton />
          </Datagrid>
          <Pagination />
        </>
      )}
    </Box>
  );
};

export const UpdateChannelUnitsTab: FC<UpdateChannelUnitsTabProps> = ({ channelId }) => (
  <Box sx={{ mt: 2 }}>
    <ListBase
      resource="units"
      storeKey={`update_channel_${channelId}_units`}
      filter={{ update_channel_id: channelId }}
      perPage={25}
      sort={{ field: 'serial', order: 'ASC' }}
    >
      <UnitsListContent channelId={channelId} />
    </ListBase>
  </Box>
);
